import type { ReactNode } from "react";
import { useCallback } from "react";
import type { PaperProps } from "@mui/material";
import { createFilterOptions, Paper } from "@mui/material";
import type { Poste } from "models";
import { PosteBox } from "components/Business";
import { stringUtil } from "@sdeapps/react-core";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { usePostes } from "hooks";
import { withComponentErrorBoundary } from "utils/errorHandling";
import { multiAutoClassNames } from "utils";
import { PosteVacantRadioGroup } from "./PosteVacantRadioGroup";

interface PosteMultiAutoProps {
  value: Array<Poste>;
  onChange?: (postes: Array<Poste>) => void;
  onlyPostesVacants: boolean | undefined;
  onOnlyPostesVacantsChange?: (value?: boolean) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
}

const filterOptions = createFilterOptions({
  trim: true,
  stringify: ({ agent: { matricule, nom, prenom }, libelle, codeAstre }: Poste) =>
    stringUtil.normalize(`${matricule} ${nom} ${prenom} ${nom} ${codeAstre} ${libelle}`),
});

interface PaperWithRadioGroupProps extends PaperProps {
  onlyPostesVacants: boolean | undefined;
  onOnlyPostesVacantsChange?: (value?: boolean) => void;
}

function PaperWithRadioGroup({
  onlyPostesVacants,
  onOnlyPostesVacantsChange,
  ...props
}: Readonly<PaperWithRadioGroupProps>): ReactNode {
  return (
    <Paper
      variant="outlined"
      {...props}
      onMouseDown={(e) => {
        // Zone dangereuse: cet override est nécessaire pour accepter les clicks dans le composant
        // `<PosteVacantRadioGroup />`, sans quoi les options perdant le _focus_, le comportement MUI
        // par défaut ferme la présentation des options.
        e.preventDefault();
        e.stopPropagation();
      }}>
      <Paper
        variant="outlined"
        sx={{ px: 1, border: 0, borderRadius: 0, borderBottom: 1, borderColor: "divider" }}>
        <PosteVacantRadioGroup isVacant={onlyPostesVacants} onChange={onOnlyPostesVacantsChange} />
      </Paper>
      {props.children}
    </Paper>
  );
}

function PosteMultiAuto({
  value,
  onChange,
  onlyPostesVacants,
  onOnlyPostesVacantsChange,
  label,
  placeholder,
  size = "medium",
}: Readonly<PosteMultiAutoProps>): ReactNode {
  const innerPaperComponent = useCallback(
    (props: PaperProps) => (
      <PaperWithRadioGroup
        {...props}
        onlyPostesVacants={onlyPostesVacants}
        onOnlyPostesVacantsChange={onOnlyPostesVacantsChange}
      />
    ),
    [onlyPostesVacants, onOnlyPostesVacantsChange]
  );
  const { postes, isPostesLoading } = usePostes();
  const filteredPoste =
    onlyPostesVacants != null ? postes.filter((p) => p.isVacant === onlyPostesVacants) : postes;

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={filteredPoste}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Poste"
      isLoading={isPostesLoading}
      label={label}
      placeholder={placeholder}
      renderOption={(props, poste, { selected }) => (
        <li {...props} key={poste.codeAstre}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          <PosteBox poste={poste} noWrap />
        </li>
      )}
      isOptionEqualToValue={(o, v) => o.codeAstre === v.codeAstre}
      getOptionKey={({ codeAstre }) => codeAstre}
      disableCloseOnSelect={true}
      slots={{ paper: innerPaperComponent }}
    />
  );
}

export const PosteMultiAutoWithErrorBoundary = withComponentErrorBoundary(PosteMultiAuto);
